import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, Package } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      setPaymentInfo(res.data);

      if (res.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (res.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      setStatus('error');
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center" data-testid="checkout-success-page">
        {status === 'loading' && (
          <div className="space-y-4">
            <Loader2 className="h-16 w-16 text-[#78BE20] mx-auto animate-spin" />
            <h1 className="text-2xl font-black text-foreground uppercase tracking-tight">
              Processing Payment
            </h1>
            <p className="text-muted-foreground">
              Please wait while we confirm your payment...
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-6">
            <CheckCircle className="h-20 w-20 text-[#78BE20] mx-auto" />
            <h1 className="text-3xl font-black text-foreground uppercase tracking-tight">
              Payment Successful!
            </h1>
            <p className="text-muted-foreground text-lg">
              Thank you for your order. You will receive a confirmation email shortly.
            </p>
            {paymentInfo && (
              <div className="bg-card border border-border rounded-sm p-6 text-left">
                <h2 className="font-bold text-foreground mb-4">Order Details</h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Amount Paid</span>
                    <span className="font-mono text-[#78BE20]">
                      ${(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <span className="text-[#78BE20] font-medium">Confirmed</span>
                  </div>
                </div>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/account">
                <Button className="bg-[#78BE20] text-black hover:bg-[#65a31b]">
                  <Package className="h-4 w-4 mr-2" />
                  View Orders
                </Button>
              </Link>
              <Link to="/products">
                <Button variant="outline" className="border-zinc-700">
                  Continue Shopping
                </Button>
              </Link>
            </div>
          </div>
        )}

        {(status === 'expired' || status === 'error' || status === 'timeout') && (
          <div className="space-y-6">
            <XCircle className="h-20 w-20 text-red-500 mx-auto" />
            <h1 className="text-3xl font-black text-foreground uppercase tracking-tight">
              Payment Issue
            </h1>
            <p className="text-muted-foreground text-lg">
              {status === 'expired' 
                ? 'Your payment session has expired. Please try again.'
                : status === 'timeout'
                ? 'We could not confirm your payment. Please check your email or contact support.'
                : 'There was an error processing your payment. Please try again.'}
            </p>
            <Link to="/cart">
              <Button className="bg-[#78BE20] text-black hover:bg-[#65a31b]">
                Return to Cart
              </Button>
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
}
